// external import
import { DataTable, DataTableColReorderParams, DataTableColumnResizeEndParams, DataTablePageParams } from "primereact/datatable";
import React, { useState, useEffect, useRef, useCallback, useMemo, useLayoutEffect } from "react";
import { Column, ColumnProps } from "primereact/column";
import ResizeObserver from "resize-observer-polyfill";
import { ContextMenu } from "primereact/contextmenu";
import { useTranslation } from "react-i18next";

// internal import
import { AdvanceFiltersWrapper } from "../components/pop-ups/advance-filters";
import { EmptyMessage } from "../components/empty-message/empty-message";
import { MemoRowGroupHeader } from "../components/row-group-header";
import { sweetConfirm } from "../../sweet-alert/sweetConfirm";
import hasPermission from "utils/hasPermissions";
import { tableUrls } from "../utils/table-urls";

// table components
import { PrimeContextMenu } from "../components/contextMenu/context-menu";
import { DefaultHeader } from "../components/default-header";
import { DefaultCell } from "../components/default-cell";

import { useDebouncedCallback } from "hooks"; // hooks

// interfaces
import * as FI from "../interfaces/filter-interfaces"; // internal filters interfaces import
import * as I from "../interfaces"; // internal table interfaces import
import * as H from "../helpers"; // helpers import

// internal style import
import "./../style.scss";

// TODO! wyświetlanie odpowiednich filtrów i input'ów w additionalColumns (flaga że kolumna jest additional?????)
// one record in tbody should have minimum 31px, if this changes you have to update this variable.
const RECORDS_HEIGHT: number = 31;
export const PrimeDataTable = <T extends I.IBaseRecord = I.IBaseRecord>(props: I.IPrimeDataTable<T>) => {
    const {
        dataSet = [],
        columns,
        customHeader,
        tableName,
        dynamicColumns = false,
        // start visual settings section
        className = "",
        children,
        btnContainerClassName = "primetable-btn-container",
        showGridlines = true,
        reorderColumnsEnabled = true,
        resizableColumnsEnabled = true,
        minimumColWidth = 123, // 123 is a perfect number for table to scalable in normal and scroll mode
        defaultTableHeight = 155, // to get a table height as a whole you need to subtract 85px and height of buttons
        disableHeadersTranslation = false,
        // end visual settings section

        // start pagination section
        meta,
        onReload = undefined,
        rowsPerPage = [5, 10, 15, 25, 50],
        // end pagination section
        // start select record section
        selectedRecords = undefined,
        onSelect = undefined,
        notSelectFirst = false,
        selectionMode = "multiple",
        idSelector = "id",
        // end select record section

        // start sorting and grouping section
        sortable = true,
        selectChoices = undefined,
        higherDependencyId = [],
        onParamsChange,
        groupRowsEnabled = true,
        customRowGroupHeader = undefined,
        customRowGroupFooter = undefined,
        useFetchTableData = false,
        // end sorting and grouping section

        // start advanced filter section
        advancedFiltersConfig = undefined,
        // end advanced filter section

        // start context menu section, if any prop below is enabled or has correct data, then context menu will be enabled and context menu item will show when right clicked on table
        auditData = undefined,
        exportCSVEnabled = false,
        hidingColumnEnabled = true,
        filterEnabled = true,
        frozenColumnEnabled = true,
        translateDisabled = false,
        additionalColumnsEnabled = false,
        contextMenuItems = [],
        onContextColumnFieldChange = undefined,
        // end context menu section

        handleDoubleClick = undefined,

        // start edit section
        onEditChange = undefined,
        cellEditEnabled = false,
        extraEditParams = {},
        firstPartEditUrl = "",
        permCode = undefined,
        customRefresh = undefined,
        refreshCallBackFunctions: refreshCallBack = [],
        customHandleEditSubmit = undefined,
        hideSuccessToast = false,
        // start edit section
    } = props;
    const { t } = useTranslation();

    const tableContainerRef = useRef<HTMLDivElement>(null);
    const btnContainerRef = useRef<HTMLDivElement>(null);
    const contextMenuRef = useRef<ContextMenu>(null);

    const [filterFocusedFieldName, setFilterFocusedFieldName] = useState<{ fieldName: string; tableName: string }>({
        fieldName: "",
        tableName: "",
    }); // handle input field name for solving focus loss problem

    const [selectedContextRecord, setSelectedContextRecord] = useState<T[]>([]);
    const _selectedContextRecord: T = selectedRecords ? selectedRecords?.[0] : selectedContextRecord?.[0];

    // Meta data
    const [paginationMeta, setPaginationMeta] = useState<I.IPrimeMeta>({
        first: 0,
        rows: meta?.pageSize,
        page: 1,
        totalRecords: meta?.count,
    });

    // Param data, this variable stores data about filters, sort and grouping
    const [params, setParams] = useState<I.IPrimeParams>({ filters: undefined, multiSortMeta: undefined });

    // Column Data, this variable stores data about currently visible columns and default(but translated) columns
    const [generatedColumns, setGeneratedColumns] = useState<I.IGeneratedPrimeColumns>({
        visible: [],
        default: [],
        columnGenerateStatus: "NOT_GENERATED_COLUMNS",
    });

    const [tableConfig, setTableConfig] = useState<I.ITableConfig>({
        contextItemFilterEnable: false,
        filtersEnabled: true,
        frozenColumn: undefined,
        tableHeight: defaultTableHeight,
    });

    const [scrollConfig, setScrollConfig] = useState<I.IScrollConfig | undefined>(undefined);
    const [isBusy, setIsBusy] = useState<boolean>(false);

    //! USEMEMO SECTION START
    const tableEnabledFunctionality: I.ITableEnabledFunctionality = useMemo(() => {
        const _sortable: boolean = !!onReload && sortable;

        return {
            pagination: !!onReload && !!paginationMeta && !!paginationMeta.rows,
            groupingEnabled: _sortable && groupRowsEnabled,
            groupingSelected: _sortable && groupRowsEnabled && !!params.multiSortMeta?.[0]?.groupByThisField,
            sortable: _sortable,
        };
    }, [onReload, sortable, !!paginationMeta, paginationMeta.rows, params?.multiSortMeta, groupRowsEnabled]);

    const tableClassName: string = useMemo((): string => {
        const frozenColumnClass = tableConfig.frozenColumn ? "frozen-mode" : "";
        const verticalScrollClass = `vertical-scroll ${scrollConfig?.vertical ? "enabled" : "disabled"}`;
        const disableLastColResize = !scrollConfig?.horizontal ? "disable-last-col-resize" : "";
        const filtersEnabled = tableConfig.filtersEnabled ? "filters-enabled" : "";

        return `${frozenColumnClass} ${verticalScrollClass} ${disableLastColResize} ${filtersEnabled}`;
    }, [tableConfig.frozenColumn, tableConfig.filtersEnabled, scrollConfig]);
    //! USEMEMO SECTION STOP

    //scroll useEffect
    useEffect(() => {
        H.setCssVariable(tableContainerRef, "--table-min-height", `${defaultTableHeight}px`);
    }, []);

    useEffect(() => {
        if (!btnContainerRef.current) return;

        const resizeObserver = new ResizeObserver(() => {
            H.setCssVariable(tableContainerRef, "--btn-container-height", `${btnContainerRef.current?.clientHeight}px`);
        });

        resizeObserver.observe(btnContainerRef.current);

        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        Array.isArray(selectedRecords) && onSelect && H.setSelected(dataSet, notSelectFirst, onSelect, selectedRecords);

        // checks if vertical scroll should be enabled when dataSet changes, but only when scrollConfig already exist,
        // because before that visible columns which are needed for calculation does not exist
        scrollConfig && handleScrollConfig("vertical");
    }, [JSON.stringify(dataSet)]);

    useEffect(() => {
        scrollConfig && handleScrollConfig("vertical");
    }, [selectedRecords]);

    useEffect(() => {
        if (generatedColumns.columnGenerateStatus != "GENERATED_COLUMNS") handleTableColumns();

        if (meta?.page) {
            setPaginationMeta({
                first: meta?.page < 2 ? 0 : (meta?.page - 1) * meta?.pageSize,
                rows: meta?.pageSize,
                page: meta?.page ? meta?.page : 0,
                totalRecords: meta?.count,
            });
        }
    }, [JSON.stringify(meta)]);

    useEffect(() => {
        if (generatedColumns.columnGenerateStatus != "NOT_GENERATED_COLUMNS") handleTableColumns();
    }, [columns]);

    useEffect(() => {
        // checks which scrolls should be enabled, on init when visible columns are already generated, and when visible columns changes
        if (generatedColumns.visible && generatedColumns.visible.length > 0) handleScrollConfig("default");
    }, [generatedColumns.visible]);

    useEffect(() => {
        // this event listener has to be added every time when visible columns changes
        if (generatedColumns.visible && generatedColumns.visible.length > 0)
            window.addEventListener("resize", () => handleScrollConfig("horizontal"));
        return () => window.removeEventListener("resize", () => handleScrollConfig("horizontal"));
    }, [scrollConfig]);

    useEffect(() => {
        if (params.multiSortMeta && params.multiSortMeta[0].groupByThisField) {
            setParams({ filters: undefined, multiSortMeta: [params.multiSortMeta[0]] });
        } else if (!!params.filters || !!params.multiSortMeta) {
            setParams({ filters: undefined, multiSortMeta: undefined });
        }
    }, [...higherDependencyId]);

    useLayoutEffect(() => {
        //! do usunięcia jak frozen col będzie zrobiony i wiersze nie będą możliwe do rozwijania
        const dataSetLength = dataSet?.length;

        if (dataSetLength > 0 && dataSetLength <= H.getDataSetBreakpoint(tableConfig.tableHeight, RECORDS_HEIGHT)) {
            if (scrollConfig && !scrollConfig?.vertical) {
                const verticalScroll = H.isVerticalScrollEnabled(tableContainerRef, tableConfig.tableHeight, tableConfig.filtersEnabled);

                verticalScroll && setScrollConfig({ vertical: true, horizontal: scrollConfig?.horizontal });
            }
        }
    });

    // generates request for saveTableConfig function and saves new config
    const handleSaveTableConfig = async (action: I.ListActionType) => {
        let request: I.IUserTableConfig = {
            columns: generatedColumns.visible,
            filtersEnabled: tableConfig.filtersEnabled,
        };

        switch (action.type) {
            case "columns":
                request.columns = action.payload.columns;
                break;
            case "filterEnabled":
                request.filtersEnabled = action.payload.filterEnabled;
                break;
        }

        await H.saveTableConfig(tableName, request);
    };

    // Configuring table options based on columns and user saved table config
    const handleTableColumns = async () => {
        const additionalColumns: I.IPrimeTableColumn[] = H.mapToAdditionalColumns(meta?.ExtraFields, additionalColumnsEnabled);

        // Generating default columns
        let _columns: I.IGeneratedPrimeTableColumn[] = H.mapColumnsOnInit(
            tableContainerRef,
            [...columns, ...additionalColumns],
            !disableHeadersTranslation ? t : undefined,
            minimumColWidth
        );

        const defaultColumns: I.IGeneratedPrimeTableColumn[] = _columns;
        let tableConfigResponse: I.IUserTableConfig | undefined = undefined;

        // Fetching columns, validating fetched columns with generated default columns, and if fetched columns exist, mapping default columns to user saved columns
        if (!dynamicColumns) {
            tableConfigResponse = await H.getTableConfig(tableName);

            if (tableConfigResponse && tableConfigResponse?.columns?.length > 0) {
                _columns = await H.compareColumns(
                    tableConfigResponse.columns,
                    defaultColumns,
                    meta === undefined ? undefined : () => handleSaveTableConfig({ type: "columns", payload: { columns: defaultColumns } })
                );
            } else if (_columns.length > 0) {
                // saving generated columns in DB if table config does not exist
                meta === undefined && handleSaveTableConfig({ type: "columns", payload: { columns: defaultColumns } });
            }
        }

        // saving visible columns in state
        setGeneratedColumns({
            visible: _columns,
            default: defaultColumns,
            columnGenerateStatus: meta === undefined ? "PLACEHOLDER_COLUMNS" : "GENERATED_COLUMNS",
        });

        // configure rest of the table settings
        handleTableConfig(_columns, tableConfigResponse);
    };

    const handleTableConfig = (columns: I.IGeneratedPrimeTableColumn[], fetchedTableConfig?: I.IUserTableConfig) => {
        let filtersEnabled = false;
        let tableHeight = tableConfig.tableHeight;
        let groupedColumn: I.DataTableSortMeta[] | undefined;

        // checking if filter should be opened
        if (onReload) {
            filtersEnabled = fetchedTableConfig?.filtersEnabled ? fetchedTableConfig?.filtersEnabled : tableConfig.filtersEnabled;
        }

        // if filter is opened then we have to change tableHeight and scss variable --table-min-height
        if (filtersEnabled) {
            const filterHeight: number = parseInt(H.getCssVariable(tableContainerRef, "--header-height"));
            tableHeight = filterEnabled ? tableHeight - filterHeight : tableHeight + filterHeight;

            H.setCssVariable(tableContainerRef, "--table-min-height", `${defaultTableHeight || tableHeight}px`);
        }

        let config: I.ITableConfig = { frozenColumn: undefined, contextItemFilterEnable: false, tableHeight, filtersEnabled };

        columns.forEach((col, index) => {
            // if there is even one filterBody in columns then context menu show/hide menu item will be enabled
            if (!!onReload && !!col.filterBody && !config.contextItemFilterEnable) {
                config.contextItemFilterEnable = true;
            }

            // checks if there is column that should be frozen on init, it also sets frozen col width
            if (col.frozen && !config.frozenColumn) {
                config.frozenColumn = {
                    field: col.field,
                    width: H.getFrozenColWidth(col),
                    originalPosition: index,
                };
                delete col.frozen;
            }

            // checks if there is column that should be grouped on init
            if (col.grouped && !groupedColumn) {
                groupedColumn = [{ header: col.header, field: col.field, order: 1, groupByThisField: true }];
                delete col.grouped;
            }
        });

        //if there is column that should be grouped on init then onSort function is fired
        if (groupedColumn) {
            handleSort({ multiSortMeta: groupedColumn }, true, columns);
            !useFetchTableData && onReload && onReload(undefined, true);
        }

        setTableConfig(config);
    };

    // Resize event listener, it checks if table should be in horizontal scroll mode or not
    const handleScrollConfig = useDebouncedCallback(
        (
            type: I.HandleScrollConfigType,
            tableHeight: number = tableConfig.tableHeight,
            filtersEnabled: boolean = tableConfig.filtersEnabled
        ) => {
            let _scrollConfig: I.IScrollConfig = {
                vertical: scrollConfig?.vertical || false,
                horizontal: scrollConfig?.horizontal || false,
            };

            switch (type) {
                case "horizontal":
                    _scrollConfig.horizontal = H.isHorizontalScrollEnabled(tableContainerRef, generatedColumns.visible);

                    break;
                case "vertical":
                    _scrollConfig.vertical =
                        dataSet?.length > H.getDataSetBreakpoint(tableHeight, RECORDS_HEIGHT) ||
                        H.isVerticalScrollEnabled(tableContainerRef, tableHeight, filtersEnabled);
                    break;
                default:
                    _scrollConfig = {
                        ..._scrollConfig,
                        vertical:
                            dataSet?.length > H.getDataSetBreakpoint(tableHeight, RECORDS_HEIGHT) ||
                            H.isVerticalScrollEnabled(tableContainerRef, tableHeight, filtersEnabled),
                        horizontal: H.isHorizontalScrollEnabled(tableContainerRef, generatedColumns.visible),
                    };
                    break;
            }

            if (JSON.stringify(scrollConfig) != JSON.stringify(_scrollConfig)) setScrollConfig(_scrollConfig);
        },
        75
    );

    const handlePage = (event: DataTablePageParams) => {
        // this if is necessary because onPage was called twice when changing pageSize,
        // once when pageSize was changed, and second time when meta with new pageSize came
        if (event.page + 1 == paginationMeta?.page && event.rows == paginationMeta.rows) return;
        const { filters, multiSortMeta } = params;

        const urlParams = H.generateUrlParams({
            page: event.page + 1,
            rows: event.rows,
            multiSortMeta: mapMultiSortMeta(multiSortMeta),
            filters,
        });

        onReload && onReload(urlParams); // triggers dataReload
    };

    const handleSort = async (event, group: boolean = false, columns: I.IGeneratedPrimeTableColumn[] | undefined = undefined) => {
        const paramsSort = params.multiSortMeta;
        let eventSort: any | undefined = event.multiSortMeta;

        if (eventSort?.length == 0) eventSort = undefined;
        else if (!group && paramsSort?.[0].groupByThisField && eventSort[0].order != paramsSort[0].order) {
            eventSort = eventSort.map((s, index) => (index == 0 ? { ...paramsSort[0], order: eventSort[0].order } : s));
        }

        const urlParams = H.generateUrlParams({
            rows: paginationMeta?.rows,
            multiSortMeta: mapMultiSortMeta(eventSort, columns),
            filters: params.filters,
        });

        setParams({ ...params, multiSortMeta: eventSort });

        onReload && onReload(urlParams, undefined); // triggers dataReload
        onParamsChange && onParamsChange({ ...params, multiSortMeta: eventSort });
    };

    const mapMultiSortMeta = (
        multiSortMeta?: I.DataTableMultiSortMetaType,
        columns: I.IGeneratedPrimeTableColumn[] = generatedColumns.visible
    ) => {
        if (!multiSortMeta) return multiSortMeta;
        const filteredVisibleColumns = columns.filter((col) => col.sortFieldName);

        let mappedMultiSortMeta: I.DataTableSortMeta[] = multiSortMeta;

        if (filteredVisibleColumns.length > 0) {
            mappedMultiSortMeta = multiSortMeta.map((sortCol) => {
                const visibleCol = filteredVisibleColumns.find((visibleCol) => visibleCol.field == sortCol.field);
                return { ...sortCol, field: visibleCol ? (visibleCol?.sortFieldName as string) : sortCol?.field };
            });
        }

        return mappedMultiSortMeta;
    };

    const handleFilterChange = useCallback(
        (input: I.IPrimeFilterInput, filterType: FI.FilteringType) => {
            let { filters, multiSortMeta } = params;
            let { name, value } = input;

            if (name.includes(".")) name = H.convertDotsToUnderscores(name);

            if (!!value) {
                filters = { ...filters, [name]: [{ value, filteringType: filterType }] };
            } else if (filters?.[name]) {
                if (Object.keys(filters).length == 1) filters = undefined;
                else delete filters[name];
            }

            setParams({ ...params, filters });
            const urlParams = H.generateUrlParams({ rows: paginationMeta?.rows, multiSortMeta: mapMultiSortMeta(multiSortMeta), filters });

            onReload && onReload(urlParams); // triggers dataReload
            onParamsChange && onParamsChange({ ...params, filters });
        },
        [params.filters, params.multiSortMeta, paginationMeta.rows, ...higherDependencyId, ...refreshCallBack]
    );

    const handleDateFilterChange = useCallback(
        (input: I.IPrimeFilterInput) => {
            let { name, value } = input;

            if (name.includes(".")) name = H.convertDotsToUnderscores(name);
            setParams({ ...params, filters: { ...params?.filters, [name]: value } });
        },
        [params.filters, params.multiSortMeta, ...higherDependencyId, ...refreshCallBack]
    );

    const handleCalendarClose = useCallback(
        (name: string) => {
            let { filters, multiSortMeta } = params;
            if (!filters || !filters[name] || !paginationMeta) return;

            const { page, rows } = paginationMeta;

            const urlParams = H.generateUrlParams({ page, rows, multiSortMeta: mapMultiSortMeta(multiSortMeta), filters });
            onReload && onReload(urlParams); // triggers dataReload
            onParamsChange && onParamsChange(params);
        },
        [params.filters, params.multiSortMeta, paginationMeta.page, paginationMeta.rows, ...higherDependencyId, ...refreshCallBack]
    );

    const handleClearDateFilter = useCallback(
        (name: string) => {
            const { page, rows } = paginationMeta;
            let { multiSortMeta, filters } = params;

            if (!paginationMeta || !filters) return;
            if (name.includes(".")) name = H.convertDotsToUnderscores(name);

            if (Object.keys(filters).length == 1 && filters?.[name]) filters = undefined;
            else delete filters[name];

            setParams({ ...params, filters });
            const urlParams = H.generateUrlParams({ page, rows, multiSortMeta: mapMultiSortMeta(multiSortMeta), filters });
            onReload && onReload(urlParams); // triggers dataReload
            onParamsChange && onParamsChange({ ...params, filters });
        },
        [params.filters, params.multiSortMeta, paginationMeta.page, paginationMeta.rows, ...higherDependencyId, ...refreshCallBack]
    );

    const handleEditSubmit = async (submitEvent: I.EditSubmitEventType) => {
        const {
            row,
            e,
            secondPartUrl = "",
            extraColumnEditParams,
            refresh,
            clientSideEdit,
            mapPatchResponse,
            editUniqueUrl = undefined,
            // onPress,
            reqParams,
        } = submitEvent;

        const url = editUniqueUrl ? editUniqueUrl : firstPartEditUrl + secondPartUrl;

        if (customHandleEditSubmit) {
            const urls = { tableEditUrl: firstPartEditUrl, inputEditUrl: secondPartUrl, editUniqueUrl: editUniqueUrl };

            const response = await customHandleEditSubmit(e, row, urls, extraColumnEditParams);

            return response;
        }

        if (!clientSideEdit && !!url) {
            const extraParams = { ...extraColumnEditParams, ...extraEditParams };
            const { filters, multiSortMeta } = params;

            const response = await H.onSubmitEdit<T>(
                row,
                e,
                url,
                dataSet,
                extraParams,
                refresh,
                mapPatchResponse,
                hideSuccessToast,
                onEditChange,
                reqParams
            );

            if ((refresh || filters || multiSortMeta) && onReload && response?.status?.toString()[0] == "2") {
                await onReload(undefined, true);
            }

            return response;
        } else {
            H.onClientSideEdit<T>(row, e, dataSet, mapPatchResponse, onEditChange);
        }
    };

    const handleAdditionalColumnEditSubmit = async (submitEvent: I.EditSubmitEventType) => {
        const { row, e, secondPartUrl = "", extraColumnEditParams, mapPatchResponse, editUniqueUrl = undefined, reqParams } = submitEvent;

        let response;

        if (customHandleEditSubmit) {
            const urls = {
                tableEditUrl: firstPartEditUrl,
                inputEditUrl: secondPartUrl,
                editUniqueUrl: editUniqueUrl,
                additionalColumnsUrl: tableUrls.additionalColumnCellEdit,
            };

            response = await customHandleEditSubmit(e, row, urls, extraColumnEditParams);
        } else {
            const { filters, multiSortMeta } = params;

            response = await H.onSubmitEdit<T>(
                row,
                e,
                tableUrls.additionalColumnCellEdit,
                dataSet,
                { modelLabel: meta?.ModelInfo?.appModelLabel },
                true,
                mapPatchResponse,
                hideSuccessToast,
                undefined,
                reqParams
            );

            if ((filters || multiSortMeta) && onReload && response?.status?.toString()[0] == "2") {
                await onReload(undefined, true);
            }
        }

        return response;
    };

    const handleFocusSelectRecord = (row) => {
        if (selectedRecords?.[0] != row && onSelect && !isBusy) onSelect([row]);
    };

    const handleHideColumns = (newVisibleColumns: I.IGeneratedPrimeTableColumn[], allHiddenColumns: I.IGeneratedPrimeTableColumn[]) => {
        const mappedColumns = H.mapColumnsWidth(newVisibleColumns, tableContainerRef);
        setGeneratedColumns({ ...generatedColumns, visible: mappedColumns });

        if (!dynamicColumns) handleSaveTableConfig({ type: "columns", payload: { columns: mappedColumns } }); // saves columns change 1in config
        if (H.shouldClearParamsAndRefresh(allHiddenColumns, params)) {
            const { page, rows } = paginationMeta;
            const urlParams = H.generateUrlParams({ page, rows });

            setParams({ filters: undefined, multiSortMeta: undefined });
            onReload && onReload(urlParams); // triggers dataReload
        }
    };

    const handleGroupBy = (column: I.IGeneratedPrimeTableColumn | undefined) => {
        let multiSortMeta: I.DataTableMultiSortMetaType = [];

        if (column) multiSortMeta = [{ header: column.header, field: column.field, order: 1, groupByThisField: true }];
        handleSort({ multiSortMeta }, true);
    };

    const handleRestoreColumns = async () => {
        if (!(await sweetConfirm("Restore default columns", "This operation is not reversible."))) return;

        const additionalColumns: I.IPrimeTableColumn[] = H.mapToAdditionalColumns(meta?.ExtraFields, additionalColumnsEnabled);

        const _columns = H.mapColumnsOnInit(tableContainerRef, [...columns, ...additionalColumns], t, minimumColWidth);
        handleTableConfig(_columns);

        setGeneratedColumns({ ...generatedColumns, visible: _columns, default: _columns });

        !dynamicColumns && handleSaveTableConfig({ type: "columns", payload: { columns: _columns } }); // saves columns change  in config
    };

    const handleToggleFilters = () => {
        const _filterEnabled = !tableConfig.filtersEnabled;
        const tableHeight = tableConfig.tableHeight;

        let filterHeight: number = parseInt(H.getCssVariable(tableContainerRef, "--header-height"));
        const newTableHeight: number = _filterEnabled ? tableHeight - filterHeight : tableHeight + filterHeight;

        H.setCssVariable(tableContainerRef, "--table-min-height", `${newTableHeight}px`);
        handleScrollConfig("vertical", newTableHeight, _filterEnabled);
        setTableConfig({ ...tableConfig, tableHeight: newTableHeight, filtersEnabled: _filterEnabled });

        !dynamicColumns && handleSaveTableConfig({ type: "filterEnabled", payload: { filterEnabled: _filterEnabled } }); // saves filterEnabled change in config
    };

    const handleFreezeColumn = (frozenColumn?: I.IGeneratedPrimeTableColumn, originalPosition?: number) => {
        setTableConfig({
            ...tableConfig,
            frozenColumn:
                !!frozenColumn && originalPosition != undefined
                    ? {
                          field: frozenColumn.field,
                          width: H.getFrozenColWidth(frozenColumn),
                          originalPosition: originalPosition,
                      }
                    : undefined,
        });
    };

    const handleColumnResizeEnd = (event: DataTableColumnResizeEndParams) => {
        // True if column that is resized is also frozen
        if (tableConfig.frozenColumn && tableConfig.frozenColumn.field == event.column.field) {
            const frozenColumn = { ...tableConfig.frozenColumn, width: `${parseInt(tableConfig.frozenColumn?.width) + event.delta}px` };
            setTableConfig({ ...tableConfig, frozenColumn: frozenColumn });
        } else {
            const resizedColumns = H.handleColumnResize(generatedColumns.visible, event, tableContainerRef, tableConfig.frozenColumn);
            !dynamicColumns && handleSaveTableConfig({ type: "columns", payload: { columns: resizedColumns } }); // saves columns change in config
            setGeneratedColumns({ ...generatedColumns, visible: resizedColumns });
        }
    };

    const handleColReorder = (event: DataTableColReorderParams) => {
        const reorderedColumns = H.getReorderedColumns(event, generatedColumns.visible);
        !dynamicColumns && handleSaveTableConfig({ type: "columns", payload: { columns: reorderedColumns } }); // saves columns change in config

        setGeneratedColumns({ ...generatedColumns, visible: reorderedColumns });
    };

    const handleAdditionalColumns = async (event: I.HandleAdditionalColumnsEvent) => {
        // maps old visible columns width back to number type, without px unit at the and, thanks to this, user column width will not be lost while generating new columns
        let newColumns: I.IPrimeTableColumn[] = generatedColumns.visible.map((col) => ({ ...col, width: parseFloat(col.width) }));

        switch (event.action) {
            case I.ADDITIONAL_COLUMNS_EVENT_ACTION.ADD:
                if (!event.payload.selectedContextColumnField) {
                    newColumns.push(event.payload.column);
                } else {
                    const newColumnAddIndex: number =
                        newColumns.findIndex((col) => col.field === event.payload.selectedContextColumnField) + 1;
                    newColumns.splice(newColumnAddIndex, 0, event.payload.column);
                }

                break;
            case I.ADDITIONAL_COLUMNS_EVENT_ACTION.EDIT:
                const editColumnIndex = newColumns.findIndex((col) => col.field === event.payload.column.field);

                if (editColumnIndex !== -1) newColumns[editColumnIndex] = event.payload.column;
                break;
            case I.ADDITIONAL_COLUMNS_EVENT_ACTION.DELETE:
                newColumns = newColumns.filter((col) => col.field != event.payload.extraField);
                break;
        }

        // generates new columns(but if user changed width to some columns, new width will not be changed)
        const _columns = H.mapColumnsOnInit(tableContainerRef, newColumns, t, minimumColWidth);

        // refreshing table config with new columns
        handleTableConfig(_columns);

        // saving new columns in state
        setGeneratedColumns({ ...generatedColumns, visible: _columns, default: _columns });

        // saving new columns in db
        !dynamicColumns && handleSaveTableConfig({ type: "columns", payload: { columns: _columns } });

        // refreshes whole data, this refreshes meta data and our additional columns (extra fields), which we need for context menu to work properly
        onReload && onReload(undefined, true);
    };

    const getDynamicColumn = (column: I.IGeneratedPrimeTableColumn) => {
        // TODO! dobry pomysłem było ustawianie kolumn na wejściu a nie przy każdym renderze
        const { className = "", editBody, body, filterBody, width, header, field, filterFieldName, headerClassName = "" } = column;
        const liteColumn: I.ILitePrimeTableColumn = { header: header, field };
        let columnProps: ColumnProps = { className: "", style: {}, filter: false, filterElement: undefined, body: undefined };
        let _frozen = tableConfig.frozenColumn?.field == field;

        // column className, if you want to add some class to the header do it here
        columnProps.className = `${className} ${headerClassName} ${editBody ? " editable-column" : ""} ${
            params.multiSortMeta?.length != 1 && params.multiSortMeta?.some((sort) => sort.field == liteColumn.field) ? "p-multi-sort" : ""
        } `;

        // special column className, this class has to be always at the and of the string. do not change this
        columnProps.className = columnProps.className.concat(`column-${liteColumn.field}`);

        // column width
        columnProps.style = { width: width };

        // header filter
        if (filterBody && tableConfig.filtersEnabled) {
            columnProps.filter = true;
            columnProps.filterElement = filterBody({
                column: { ...liteColumn, field: filterFieldName || field },
                onFilterChange: handleFilterChange,
                onClearDateFilter: handleClearDateFilter,
                onCalendarClose: handleCalendarClose,
                onDateFilterChange: handleDateFilterChange,
                filter: params?.filters?.[H.convertDotsToUnderscores(filterFieldName || field)]?.[0],
                filterChoices: selectChoices?.[liteColumn.field],
                filterFocusedFieldName: filterFocusedFieldName,
                setFilterFocusedFieldName: setFilterFocusedFieldName,
                filterArrayLength: (params?.filters?.[H.convertDotsToUnderscores(filterFieldName || field)] || []).length,
                tableRef: tableContainerRef,
                tableName: tableName,
            });
        } else if (tableConfig.frozenColumn && tableConfig.filtersEnabled) {
            columnProps.filter = true;
            columnProps.filterElement = <div />;
        }

        // body & editing start
        if (editBody && cellEditEnabled && (!permCode || hasPermission(permCode))) {
            columnProps.body = (row, rowProps) => {
                return (
                    <div style={{ height: "100%" }}>
                        {editBody({
                            ref: tableContainerRef,
                            row: row,
                            fieldName: liteColumn.field,
                            selectChoices: selectChoices?.[liteColumn.field],
                            handleFocusSelectRecord: handleFocusSelectRecord,
                            handleEditSubmit: handleEditSubmit,
                            translate: (value, config) => t(value, config),
                            isBusy: isBusy,
                            customRefresh: customRefresh,
                            modelInfo: meta?.ModelInfo,
                            refresh: onReload ? () => onReload(undefined, true) : undefined,
                            permCode: permCode,
                            rowIndex: (rowProps as any).rowIndex,
                        })}
                    </div>
                );
            };
        } else if (body) {
            columnProps.body = (row) => {
                return (
                    <div style={{ height: "100%" }} key={`${liteColumn.field}-${tableName}`}>
                        {body({
                            row: row,
                            fieldName: liteColumn.field,
                            translate: (value, config) => t(value, config),
                            tableRef: tableContainerRef,
                        })}
                    </div>
                );
            };
        } else if (column.additionalType) {
            const additionalType = column.additionalType;

            if (tableConfig.filtersEnabled) {
                const _field = H.additionalColumnFilterField(filterFieldName || field);

                const filterData: FI.IPrimeFilterData = {
                    column: { ...liteColumn, field: _field },
                    onFilterChange: handleFilterChange,
                    onClearDateFilter: handleClearDateFilter,
                    onCalendarClose: handleCalendarClose,
                    onDateFilterChange: handleDateFilterChange,
                    filter: params?.filters?.[H.convertDotsToUnderscores(_field)]?.[0],
                    filterChoices: selectChoices?.[liteColumn.field],
                    filterFocusedFieldName: {
                        ...filterFocusedFieldName,
                        fieldName: H.additionalColumnFilterField(filterFocusedFieldName.fieldName),
                    },
                    setFilterFocusedFieldName: setFilterFocusedFieldName,
                    filterArrayLength: (params?.filters?.[H.convertDotsToUnderscores(_field)] || []).length,
                    tableRef: tableContainerRef,
                    tableName: tableName,
                };

                columnProps.filter = true;
                columnProps.filterElement = H.additionalColumnFilterBody(filterData, additionalType);
            }

            columnProps.body = (row, rowProps) => {
                const inputData: I.IPrimeEditData<T> = {
                    ref: tableContainerRef,
                    row: row,
                    fieldName: liteColumn.field,
                    selectChoices: selectChoices?.[liteColumn.field],
                    handleFocusSelectRecord: handleFocusSelectRecord,
                    handleEditSubmit: handleAdditionalColumnEditSubmit,
                    translate: (value, config) => t(value, config),
                    isBusy: isBusy,
                    customRefresh: customRefresh,
                    modelInfo: meta?.ModelInfo,
                    refresh: onReload ? () => onReload(undefined, true) : undefined,
                    rowIndex: (rowProps as any).rowIndex,
                    permCode: permCode,
                };

                return H.additionalColumnEditBody(inputData, additionalType);
            };
        } else {
            columnProps.body = (row) => (
                <DefaultCell key={`${liteColumn.field}-${tableName}`}>{H.getNestedObjValue(row, liteColumn.field.split("."))}</DefaultCell>
            );
        }

        // body & editing end
        return (
            <Column
                header={customHeader ? customHeader(column) : <DefaultHeader liteColumn={liteColumn} />}
                key={`${liteColumn.field}-${tableName}`}
                field={field}
                sortable={column.sortable} // sorting
                frozen={_frozen} // frozen column
                {...columnProps}
            />
        );
    };

    const handleAdvanceFilterReload = (filters: I.IPrimeTableFilters | undefined) => {
        let urlParams: string = H.generateUrlParams({
            rows: paginationMeta?.rows,
            multiSortMeta: mapMultiSortMeta(params.multiSortMeta),
            filters: filters,
        });

        setParams({ ...params, filters });
        onReload && onReload(urlParams); // triggers dataReload
    };

    const handleSelectedContextRecord = (e) => {
        if (onSelect && (selectedRecords?.length != 1 || e.value?.id != selectedRecords[0]?.[idSelector])) onSelect([e.value]);
        else if (!onSelect && e.value?.id != selectedContextRecord[0]?.[idSelector]) setSelectedContextRecord([e.value]);
    };

    return (
        <div className="prime-data-table-outer-wrapper">
            {(children || advancedFiltersConfig) && (
                <div className={btnContainerClassName} ref={btnContainerRef}>
                    {children}

                    <AdvanceFiltersWrapper
                        onReload={handleAdvanceFilterReload}
                        tableFilters={params.filters}
                        advancedFiltersConfig={advancedFiltersConfig as I.IAdvancedFilterConfig[]}
                    />
                </div>
            )}
            <PrimeContextMenu
                // Props needed for context-menu
                contextMenuRef={contextMenuRef}
                contextMenuItems={contextMenuItems}
                // Props that provide data
                auditData={auditData}
                selectedRecord={_selectedContextRecord}
                dataSet={dataSet}
                multiSort={params.multiSortMeta?.[0]}
                tableConfig={tableConfig}
                meta={meta}
                // Enabled/disabled
                exportCSVEnabled={exportCSVEnabled && dataSet.length > 0}
                hidingColumnEnabled={hidingColumnEnabled}
                groupRowsEnabled={tableEnabledFunctionality.groupingEnabled}
                filterEnabled={filterEnabled && tableConfig.contextItemFilterEnable}
                frozenColumnEnabled={frozenColumnEnabled && scrollConfig?.horizontal}
                translateDisabled={translateDisabled}
                additionalColumnsEnabled={additionalColumnsEnabled}
                // Columns
                defaultColumns={generatedColumns.default}
                visibleColumns={generatedColumns.visible}
                // handlers
                onRestoreColumns={handleRestoreColumns}
                onHideColumns={handleHideColumns}
                onToggleFilters={handleToggleFilters}
                onFreezeColumn={handleFreezeColumn}
                onGroupBy={handleGroupBy}
                onContextColumnFieldChange={onContextColumnFieldChange}
                onAdditionalColumns={handleAdditionalColumns}
            />
            <div ref={tableContainerRef} className={`${className} prime-table-container`}>
                {scrollConfig != undefined && (
                    <DataTable
                        value={dataSet}
                        // Display and styles
                        className={`p-datatable-sm ${tableClassName}`}
                        showGridlines={showGridlines}
                        scrollHeight={`${tableConfig.tableHeight}px`}
                        scrollable={scrollConfig.horizontal || scrollConfig.vertical}
                        emptyMessage={<EmptyMessage t={t} />}
                        frozenWidth={tableConfig.frozenColumn?.width}
                        // Column reorder
                        reorderableColumns={reorderColumnsEnabled}
                        onColReorder={handleColReorder}
                        // Column resize
                        resizableColumns={resizableColumnsEnabled && generatedColumns.visible.length > 1}
                        columnResizeMode="expand"
                        onColumnResizeEnd={handleColumnResizeEnd}
                        // Context Menu
                        contextMenuSelection={_selectedContextRecord?.[0]}
                        onContextMenuSelectionChange={handleSelectedContextRecord}
                        onContextMenu={(e) => contextMenuRef?.current && contextMenuRef.current.show(e.originalEvent)}
                        // Selection
                        {...(Array.isArray(selectedRecords) &&
                            onSelect && {
                                selection: selectedRecords,
                                onSelectionChange: (selected) =>
                                    !isBusy && H.onSelection(selected, dataSet, selectedRecords, onSelect, idSelector),
                                selectionMode: selectionMode,
                            })}
                        // Server side pagination
                        {...(tableEnabledFunctionality.pagination && {
                            paginator: true,
                            first: paginationMeta.first,
                            rows: paginationMeta.rows,
                            totalRecords: paginationMeta.totalRecords,
                            rowsPerPageOptions: rowsPerPage,
                            onPage: handlePage,
                            lazy: true,
                        })}
                        // Grouping
                        {...(tableEnabledFunctionality.groupingSelected && {
                            rowGroupMode: "subheader",
                            groupField: params.multiSortMeta?.[0].field,
                            rowGroupHeaderTemplate: customRowGroupHeader
                                ? customRowGroupHeader
                                : (row) => (
                                      <MemoRowGroupHeader
                                          frozenColumn={!!tableConfig.frozenColumn}
                                          row={row}
                                          groupField={params.multiSortMeta?.[0]}
                                          t={t}
                                      />
                                  ),
                            rowGroupFooterTemplate: customRowGroupFooter ? customRowGroupFooter : () => <td />,
                        })}
                        // Group and sort
                        {...(tableEnabledFunctionality.sortable && {
                            onSort: handleSort,
                            removableSort: true,
                            sortMode: "multiple",
                            multiSortMeta: params?.multiSortMeta,
                        })}
                        onRowDoubleClick={(e) => handleDoubleClick && handleDoubleClick(e)}
                    >
                        {generatedColumns.visible.map((col) => getDynamicColumn(col))}
                    </DataTable>
                )}
            </div>
        </div>
    );
};
